import { Component, EventEmitter, OnDestroy, OnInit } from '@angular/core';
import { Unit, UnitService } from '../../../helper/anessa-library/anessa-library/projects/unit-library/src/public-api';
import { ProjectCommunicationService } from '../../../communication/project-comms.service';
import { ConfiguredToastrService } from '../../../helper/toastr/configured-toastr.service';
import { take, takeUntil, takeWhile } from 'rxjs/operators';
import ResultsUtils, { PeriodUnitMapper } from '../../results-util';
import Utils from '../../../util';
import { ResultRow } from '../../../models/data-structures/results/result-row.model';
import { ScenarioResultsService } from './scenario-results-helper.service';
import { FeedstockService } from '../../../project/feedstock/feedstock.service';
import { MatDialog } from '@angular/material/dialog';
import {
  ScenarioResultDropdownSearchComponent
} from './scenario-results-dropdown-search/result-dropdown-search.component';
import { AnessaTranslationInterface } from '../../../models/interfaces/anessa-translation.interface';
import { AnessaTranslationService } from '../../../helper/translation/anessa-translation.service';
import { DropdownDisplayItem } from '../../../models/data-structures/dropdown-display-item';
import { AggregationMethod } from '../../results-helper/grouped-result-table/grouped-result-table.component';
import { UserpilotReloader } from '../../../helper/userpilot-reloader.component';
import { Results } from '../../../models/results/results.model';
import { LooseObject } from '../../../models/loose-object.model';
import { FinancialResults } from '../../../models/results/financial/financial.model';
import { TechnicalResultsGeneral } from '../../../models/results/technical/general/general-tech-results.model';
import { Feedstock } from '../../../models/project/feedstock/feedstock.model';
import { Project } from '../../../models/project/project.model';
import { PieData, PlotlyTrace } from '../../../models/results/plotly.model';
import { FeedstockBreakdownDataItem } from '../../../models/data-structures/results/feedstock-breakdown/feedstock-breakdown-data-item.model';
import { Scenario } from '../../../models/results/scenario-comparison/scenario.model';

export enum ResultCategory {
  TECHNICAL = 'RESULTS.DATA.TECHNICAL.TITLE',
  BREAKDOWN = 'RESULTS.SECTIONS.FEEDSTOCK_BREAKDOWN',
  GENERAL = 'RESULTS.SECTIONS.FINANCIAL_GENERAL',
  METRICS = 'RESULTS.SECTIONS.FINANCIAL_METRICS',
  DEBT = 'RESULTS.SECTIONS.DEBT_SCHEDULE',
  INCOME = 'RESULTS.SECTIONS.INCOME_STATEMENT',
  CASHFLOW = 'RESULTS.SECTIONS.CASH_FLOW_ANALYSIS'
}

@Component({
  selector: 'app-scenario-results',
  templateUrl: './scenario-results.component.html',
  styleUrls: ['./scenario-results.component.scss'],
  providers: [FeedstockService, ScenarioResultsService]
})
export class ScenarioResultsComponent extends UserpilotReloader implements OnInit, OnDestroy, AnessaTranslationInterface {
  onDestroyNotifier = new EventEmitter();
  Object = Object;
  scenarios: Scenario[] = [];
  resultCategories = [ResultCategory.TECHNICAL, ResultCategory.BREAKDOWN,
    ResultCategory.GENERAL, ResultCategory.METRICS, ResultCategory.DEBT,
    ResultCategory.INCOME, ResultCategory.CASHFLOW];
  resultGroups: ResultGroup[] = [];
  resultItems: ResultGroup[] = [];
  baseline: Results | null = null;
  resultProject?: Project;
  selectedScenarios: string[] = [];
  selectedCategory: ResultCategory | null = null;
  selectedResultGroup?: ResultGroup | null = null;
  selectedResultItems: ResultGroup[] | null = null;
  techUnitTypes: DropdownDisplayItem[] = [
    new DropdownDisplayItem('RESULTS.GENERAL.METRIC', 'metric'),
    new DropdownDisplayItem('RESULTS.GENERAL.IMPERIAL', 'imperial')
  ];
  techUnitType = 'metric';

  appendUnit = false;

  periodCategories = [
    new DropdownDisplayItem('RESULTS.GENERAL.DAY', 'day'),
    new DropdownDisplayItem('RESULTS.GENERAL.MONTH', 'month'),
    new DropdownDisplayItem('RESULTS.GENERAL.YEAR', 'year')
  ];
  selectedPeriod = 'day';

  freqAdjustable = [ResultCategory.TECHNICAL, ResultCategory.BREAKDOWN];
  currencyAdjustable = [ResultCategory.GENERAL, ResultCategory.METRICS, ResultCategory.DEBT, ResultCategory.INCOME, ResultCategory.CASHFLOW];
  unitAdjustable = [ResultCategory.TECHNICAL, ResultCategory.BREAKDOWN];

  // headers used for table creation
  valueHeader = 'RESULTS.DATA.VALUES';
  unitHeader = 'RESULTS.DATA.UNIT';
  shareHeader = 'RESULTS.GENERAL.SHARE';
  treeTableData: Record<string, Record<string, ResultRow>> = {};
  curTableHeaders: string[] | null = [];
  curGraphData: (PlotlyTrace | PieData)[] = [];
  valueKey = this.valueHeader;
  yAxisLabel = '';
  xAxisLabel: string | null = null;

  finCurrencyReady = false;
  financialExchange: Unit[] = [];
  exchangeRateDate: string | null = null;
  selectedCurrency?: Unit | null = null;
  dataReady = false;


  // Booleans that enable and disable result things
  // bar graph only for the financial section
  showGroupBarGraph = false;
  showTreeTable = false;
  showPieChart = false;
  precision?: number | null;
  graphLayout;
  customFirstColumnHeader: string | null = null;
  trigger?: string;

  aggMethod = AggregationMethod.FIRST;
  aggWhitelist: string[] = [];

  constructor(private projectCommService: ProjectCommunicationService,
              private unitService: UnitService,
              private toastrService: ConfiguredToastrService,
              private scenarioResultsService: ScenarioResultsService,
              private feedstockService: FeedstockService,
              private matDialog: MatDialog,
              private translationService: AnessaTranslationService
  ) {
    super();
  }

  ngOnInit(): void {
    this.techUnitType = this.projectCommService.getResultProjectState()?.projectInfo?.defaults?.unitSystem || 'metric';
    // project is loaded if this service is ready
    this.scenarioResultsService.serviceReadyNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .pipe(takeWhile(x => !x, true))
      .subscribe(loaded => {
        if (loaded) {
          this.precision = this.scenarioResultsService.getProjectDecimalPrecision();
          this.setResults();
        }
      });
    this.translationService.languageChangeNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(language => {
        this.processData();
        this.trigger = language;
      });
  }

  ngOnDestroy() {
    this.onDestroyNotifier.emit();
    this.onDestroyNotifier.unsubscribe();
  }

  setResults() {
    if (!this.scenarioResultsService.scenarioComparisonHasResults()) {
      return;
    }
    // the project that was used when the scenario results were simulated
    this.resultProject = this.scenarioResultsService.getResultProject();
    this.scenarios = this.scenarioResultsService.getProcessScenarios();

    this.baseline = this.scenarioResultsService.getBaselineResults();
    if (!this.baseline) {
      this.toastrService.warning(this.translationService.get('SCENARIO_COMPARISON.RESULTS.NO_BASELINE_RESULTS'),
        this.translationService.get('SCENARIO_COMPARISON.RESULTS.RESULTS_INCOMPLETE'));
      return;
    }
    this.getFinExchange(this.baseline.created);
  }

  private checkDataReady() {
    this.dataReady = true;
    // if no baseline
    this.dataReady = !(!this.baseline || !this.hasResults(this.baseline));

    // if scenarios check each one else false
    if (this.scenarios && this.scenarios.length > 0) {
      for (const scenario of this.scenarios) {
        if (!this.dataReady || !this.hasResults(scenario.results)) {
          this.dataReady = false;
        }
      }
    } else {
      this.dataReady = false;
    }
  }

  private hasResults(obj: Results) {
    if (!this.resultProject) return false;
    if (this.resultProject.projectInfo.useFinancial) {
      return (obj && obj.technical && obj.financial);
    } else {
      return (obj && obj.technical);
    }
  }

  financialDisabled(cat: ResultCategory) {
    if (cat !== ResultCategory.TECHNICAL && cat !== ResultCategory.BREAKDOWN) {
      if (!this.projectCommService.project.projectInfo.useFinancial) {
        return true;
      }
    }
    return false;
  }


  getFinExchange(created: string | null) {
    this.unitService.getCurrencyUnits(created).then(
      (units: Unit[]) => {
        this.exchangeRateDate = this.unitService.getExchangeRateDate();
        this.financialExchange = units;

        // sort rates alphabetically
        this.financialExchange.sort((first: Unit, second: Unit) => {
          if (Number(first.order) < Number(second.order)) {
            return -1;
          } else if (Number(second.order) < Number(first.order)) {
            return 1;
          } else {
            return 0;
          }
        });
        // set default currency
        const defaults = this.projectCommService.getProjectDefaults();
        if (!this.selectedCurrency && defaults.currency) {
          this.selectedCurrency = this.financialExchange.find(curr => curr.id === defaults.currency);
        }
        if (!this.selectedCurrency) {
          this.selectedCurrency = this.financialExchange.find(curr => curr.id === '000000000000000000002004'); // CAD
        } else {
          this.selectedCurrency = this.financialExchange.find(curr => curr.id === this.selectedCurrency.id);
        }
        this.finCurrencyReady = true;
        this.setDefaultSelection();
        this.checkDataReady();
      }
    );
  }

  setDefaultSelection() {
    if (!this.baseline) {
      return;
    }
    let i = 0;
    // selected scenarios has to be set for the view to register its changes
    const tempScenarios: string[] = [];
    for (const sce of this.scenarios) {
      if (sce.results) tempScenarios.push(sce.name);
      if (i >= 9) break;
      i++;
    }
    this.selectedScenarios = tempScenarios;
    if (this.resultProject?.projectInfo.useFinancial) {
      this.selectedCategory = ResultCategory.METRICS;
      this.selectedCategoryChanged();
      this.selectedResultGroup = this.resultGroups.find(item => item.name === 'RESULTS.DATA.ECONOMIC_METRICS.TITLE');
      this.selectedGroupChanged();
      this.selectedResultItems = [];
      const npv = this.resultItems.find(i => i.name === 'RESULTS.DATA.ECONOMIC_METRICS.NPV');
      if (npv) {
        this.selectedResultItems.push(npv);
      }
      const irr = this.resultItems.find(i => i.name === 'RESULTS.DATA.ECONOMIC_METRICS.IRR');
      if (irr) {
        this.selectedResultItems.push(irr);
      }
      const spp = this.resultItems.find(i => i.name === 'RESULTS.DATA.ECONOMIC_METRICS.SPP');
      if (spp) {
        this.selectedResultItems.push(spp);
      }
      this.selectedItemsChanged();
    } else {
      this.selectedCategory = ResultCategory.TECHNICAL;
      this.selectedCategoryChanged();
      this.selectedResultGroup = this.resultGroups.find(item => item.name === 'RESULTS.DATA.TECHNICAL.SP');
      this.selectedGroupChanged();
      this.selectedResultItems = [...this.resultItems];
      this.selectedItemsChanged();
    }
  }

  isShowFrequencyDropdown() {
    return this.selectedCategory && this.freqAdjustable.includes(this.selectedCategory);
  }

  isShowCurrencyDropdown() {
    return this.selectedCategory && this.currencyAdjustable.includes(this.selectedCategory) && this.finCurrencyReady;
  }

  isShowUnitDropdown() {
    return this.selectedCategory && this.unitAdjustable.includes(this.selectedCategory);
  }


  isShowResults() {
    return (this.showTreeTable
        || this.showGroupBarGraph
        || this.showPieChart)
      && this.selectedScenarios.length > 0;
  }

  selectedCategoryChanged() {
    this.selectedResultGroup = null;
    this.selectedResultItems = [];
    this.resultGroups = [];
    this.resultItems = [];
    if (this.selectedCategory === ResultCategory.TECHNICAL) {
      this.getTechResultGroups();
    } else if (this.selectedCategory === ResultCategory.BREAKDOWN) {
      this.getResultItems();
    } else if (this.selectedCategory === ResultCategory.GENERAL) {
      this.getFinGeneralGroups();
    } else if (this.selectedCategory === ResultCategory.METRICS) {
      this.getFinancialResultGroups(ResultCategory.METRICS);
    } else if (this.selectedCategory === ResultCategory.DEBT) {
      this.getFinancialResultGroups(ResultCategory.DEBT);
    } else if (this.selectedCategory === ResultCategory.INCOME) {
      this.getFinancialResultGroups(ResultCategory.INCOME);
    } else if (this.selectedCategory === ResultCategory.CASHFLOW) {
      this.getFinancialResultGroups(ResultCategory.CASHFLOW);
    }
    this.processData();
  }

  getTechResultGroups() {
    this.resultGroups = [];
    if (!this.baseline) return;
    const keys = Object.keys(this.baseline.technical);
    for (const section of keys) {
      if (section === 'product_rates' || section === 'fuel_rates') continue;
      const subTable = this.baseline.technical[section];
      const subKeys = Object.keys(subTable);
      for (const group of subKeys) {
        // translation is happening in HTML
        const resGroup = {
          name: group,
          path: 'RESULTS.DATA.TECHNICAL.TITLE->' + String(section) + '->' + group
        };
        if (this.baseline.technical[section] instanceof TechnicalResultsGeneral) {
          const generalTechnicalResults = this.baseline.technical[section] as TechnicalResultsGeneral;
          if (group === 'RESULTS.DATA.TECHNICAL.OU') {
            for (const res of this.Object.keys(generalTechnicalResults[group])) {
              const item = {
                name: 'RESULTS.DATA.OU' + ' ' + res,
                path: 'RESULTS.DATA.TECHNICAL.TITLE->' + String(section) + '->' + group + '->' + res
              };
              this.resultGroups.push(item);
            }
            continue;
          }
        }
        this.resultGroups.push(resGroup);
      }
    }
  }

  getFinancialResultGroups(section: ResultCategory) {
    if (!this.baseline) return;

    this.resultGroups = [];
    const excluded = ['RESULTS.DATA.TOTALS'];
    const sec = this.getResultSection(section) as keyof FinancialResults;
    if (!this.baseline.financial[sec]) return;
    const subTable = this.baseline.financial[sec];
    const keys = Object.keys(this.baseline.financial[sec]) as (keyof typeof subTable)[];
    for (const group of keys) {
      const subkey = group as string;
      let pushed = false;
      // translation is happening in HTML
      const item = {
        name: subkey,
        path: 'RESULTS.DATA.FINANCIAL->' + sec + '->' + subkey
      };
      if (subkey === 'RESULTS.DATA.PARTNERSHIP') {
        for (const res of this.Object.keys(this.baseline.financial[sec][subkey])) {
          // translation is happening in HTML
          const item: ResultGroup = {
            name: res,
            path: 'RESULTS.DATA.FINANCIAL->' + sec + '->' + subkey + '->' + res,
            params: { '%s': this.baseline.financial[sec][subkey][res]['formatValues'] }
          };
          if (!excluded.includes(subkey)) {
            this.resultGroups.push(item);
            pushed = true;
          }
        }
      }
      if (!excluded.includes(subkey) && !pushed) {
        this.resultGroups.push(item);
      }
    }
  }

  getResultSection(section: ResultCategory) {
    switch (section) {
      case ResultCategory.METRICS:
        return 'RESULTS.DATA.EAFM';
      case ResultCategory.DEBT:
        return 'RESULTS.DATA.DEBT_SCHEDULE';
      case ResultCategory.INCOME:
        return 'RESULTS.DATA.ISPL.TITLE';
      case ResultCategory.CASHFLOW:
        return 'RESULTS.DATA.CASH_FLOW';
      default:
        return section;
    }
  }

  getFinGeneralGroups() {
    this.resultGroups = [
      {
        name: 'RESULTS.SECTIONS.OPEX_BREAKDOWN', graphType: 'piechart',
        dataFunc: this.scenarioResultsService.getOpexBreakdown.bind(this)
      },
      {
        name: 'RESULTS.SECTIONS.CAPEX_BREAKDOWN', graphType: 'piechart',
        dataFunc: this.scenarioResultsService.getCapexBreakdown.bind(this)
      },
      {
        name: 'RESULTS.SECTIONS.REVENUES_BREAKDOWN', graphType: 'piechart',
        dataFunc: this.scenarioResultsService.getRevenuesBreakdown.bind(this)
      },
      {
        name: 'RESULTS.SECTIONS.SELLING_PRODUCTS_REVENUE_BREAKDOWN',
        graphType: 'piechart', dataFunc: this.scenarioResultsService.getSellingProductsRevenueBreakdown.bind(this)
      },
      {
        name: 'RESULTS.SECTIONS.FEEDSTOCK_REVENUE_BREAKDOWN',
        graphType: 'piechart', dataFunc: this.scenarioResultsService.getFeedstockRevenueBreakdown.bind(this)
      },
      {
        name: 'RESULTS.SECTIONS.ADDITIONAL_INCOME_BREAKDOWN',
        graphType: 'piechart', dataFunc: this.scenarioResultsService.getAdditionalIncomeBreakdown.bind(this)
      },
      {
        name: 'RESULTS.SECTIONS.REVENUE_TREND', graphType: 'stackedbar',
        dataFunc: this.scenarioResultsService.getRevenueTrend.bind(this)
      },
      {
        name: 'RESULTS.SECTIONS.EARNINGS_TREND', graphType: 'bargroup',
        dataFunc: this.scenarioResultsService.getEarningsTrend.bind(this)
      },
    ];
  }

  selectedGroupChanged() {
    this.getResultItems();
    this.processData();
  }

  getResultItems() {
    this.selectedResultItems = [];
    // result items will be translated in HTML
    this.resultItems = [];
    if (this.selectedCategory !== ResultCategory.BREAKDOWN && this.selectedCategory !== ResultCategory.GENERAL) {
      if (!this.baseline || !this.selectedResultGroup || !this.selectedResultGroup.path) {
        return;
      }
      const separated = this.selectedResultGroup.path.split('->');
      let vals: LooseObject = this.baseline;
      for (let i = 0; i < separated.length; i++) {
        vals = vals[separated[i]];
      }
      for (const section of Object.keys(vals)) {
        if (section === 'formatValues') continue;
        let pushed = false;
        const item = {
          name: section,
          path: this.selectedResultGroup.path + '->' + section
        };
        if (!Object.keys(vals[section]).includes(this.valueHeader)) {
          for (const group of Object.keys(vals[section])) {
            const item: ResultGroup = {
              name: group,
              path: this.selectedResultGroup.path + '->' + section + '->' + group,
              params: { '%s': vals[section][group]['formatValues'] }
            };
            pushed = true;
            if (!item.path || this.allValuesAreZero(item.path)) continue;
            this.resultItems.push(item);
          }
        }
        if (!pushed) this.resultItems.push(item);
      }
    } else if (this.selectedCategory === ResultCategory.BREAKDOWN) {
      const data = this.scenarioResultsService.getFeedstockBreakdownData();
      if (!data) return;
      for (const feed of data) {
        this.resultItems.push({ name: feed.feedstock, path: null });
      }
    } else if (this.selectedCategory === ResultCategory.GENERAL) {
      if (!this.selectedResultGroup || !this.selectedResultGroup.dataFunc) {
        return;
      }
      const datapackage = this.getFinGeneralDataPackage();
      if (!datapackage) return;
      const data = this.selectedResultGroup.dataFunc(this.selectedCurrency, datapackage.baselineDataset);
      if (data.hasTableData) {
        for (const item of data.tableData) {
          this.resultItems.push({ name: item.label, path: null });
        }
      } else if (data.hasGraphData) {
        if (this.selectedResultGroup.name === 'RESULTS.SECTIONS.EARNINGS_TREND') {
          for (const item of data.graphData[0].series) {
            this.resultItems.push({ name: item.name, path: null });
          }
        } else {
          for (const item of data.graphData) {
            this.resultItems.push({ name: item.name, path: null });
          }
        }
      }
    }
  }

  /**
   * Check result in baseline and scenarios for all zeros.
   * Return false if any non zero value is found; true if all zeros.
   * @param itemPath result path of row to check in scenarios
   */
  allValuesAreZero(itemPath: string) {
    if (!this.baseline) return false;

    const separated = itemPath.split('->');
    let baseVals: LooseObject = this.baseline;
    for (let i = 0; i < separated.length; i++) {
      baseVals = baseVals[separated[i]];
    }
    if (baseVals.values) {
      for (const x of baseVals.values) {
        if (Number(x) !== 0) {
          return false;
        }
      }
    } else {
      if ('RESULTS.DATA.LABEL' in baseVals) return true;
      // variable pricing/other, 1 level deeper
      for (const key in baseVals) {
        if (baseVals[key].values) {
          for (const x of baseVals[key].values) {
            if (Number(x) !== 0) {
              return false;
            }
          }
        }
      }
    }
    for (const sce of this.scenarios) {
      let sceVals = sce.results as LooseObject;
      for (let i = 0; i < separated.length; i++) {
        sceVals = sceVals[separated[i]];
      }
      if (sceVals.values) {
        for (const x of sceVals.values) {
          if (Number(x) !== 0) return false;
        }
      } else {
        for (const key in baseVals) {
          if (baseVals[key].values) {
            for (const x of baseVals[key].values) {
              if (Number(x) !== 0) {
                return false;
              }
            }
          }
        }
      }
    }

    return true;
  }

  selectedItemsChanged() {
    this.processData();
  }

  processData() {
    this.showGroupBarGraph = false;
    this.showTreeTable = false;
    this.showPieChart = false;
    this.curTableHeaders = [];
    this.customFirstColumnHeader = null;
    this.valueKey = this.valueHeader;
    this.xAxisLabel = null;
    this.graphLayout = null;
    if (this.selectedCategory === ResultCategory.TECHNICAL) {
      this.appendUnit = true;
      this.prepareDataForTreeTable();
      this.prepareDataForGroupGraph();
    } else if (this.selectedCategory === ResultCategory.BREAKDOWN) {
      this.appendUnit = true;
      this.prepareFeedstockTableData();
      this.prepareDataForGroupPieChart();
    } else if (this.selectedCategory === ResultCategory.GENERAL) {
      this.appendUnit = false;
      this.prepareFinGeneralTableData();
      if (this.selectedResultGroup && this.selectedResultGroup.graphType === 'piechart') {
        this.prepareDataForGroupPieChart();
      } else {
        this.prepareFinGeneralGraph();
        this.xAxisLabel = this.translationService.get('RESULTS.GENERAL.YEARS');
      }
    } else if (this.selectedCategory === ResultCategory.METRICS) {
      this.appendUnit = true;
      this.prepareDataForTreeTable();
      this.prepareDataForGroupGraph();
    } else if (this.selectedCategory === ResultCategory.DEBT) {
      this.customFirstColumnHeader = this.translationService.get('RESULTS.GENERAL.YEARS');
      this.xAxisLabel = this.translationService.get('RESULTS.GENERAL.YEARS');
      this.appendUnit = false;
      this.prepareDataForTreeTable();
      this.prepareDataForGroupGraph();
    } else if (this.selectedCategory === ResultCategory.INCOME) {
      this.customFirstColumnHeader = this.translationService.get('RESULTS.GENERAL.YEARS');
      this.xAxisLabel = this.translationService.get('RESULTS.GENERAL.YEARS');
      this.appendUnit = false;
      this.prepareDataForTreeTable();
      this.prepareDataForGroupGraph();
    } else if (this.selectedCategory === ResultCategory.CASHFLOW) {
      this.customFirstColumnHeader = this.translationService.get('RESULTS.GENERAL.YEARS');
      this.xAxisLabel = this.translationService.get('RESULTS.GENERAL.YEARS');
      this.appendUnit = false;
      this.prepareDataForTreeTable();
      this.prepareDataForGroupGraph();
    }
  }

  private addAdditionalRNGRows(resultSet, row: ResultRow) {
    const label = row.label;
    resultSet[label] = {};
    resultSet[label][label] = this.convertResultRowToTargetUnit(Utils.deepCopy(row));
    if (this.techUnitType === 'imperial') {
      if (row.values && row.values[0]) {
        const baseValue = row.values[0];
        const mmbtuUnit = this.unitService.getUnitById('000000000000000000003810');
        const ggeUnit = this.unitService.getUnitById('000000000000000000003811');
        const mmbtuValue = mmbtuUnit.convertFromBase(Utils.deepCopy(baseValue));
        const ggeValue = ggeUnit.convertFromBase(Utils.deepCopy(baseValue));
        const mmbtuResultRow = new ResultRow();
        mmbtuResultRow.label = this.translationService.get('RESULTS.DATA.RNG_PRODUCTION_MMBTU');
        mmbtuResultRow.values = Utils.deepCopy(row['RESULTS.DATA.VALUES']);
        mmbtuResultRow.values[0] = mmbtuValue;
        this.convertToPeriodAndSetDisplayName(mmbtuResultRow, mmbtuUnit);
        resultSet[label]['RESULTS.DATA.RNG_PRODUCTION_MMBTU'] = mmbtuResultRow;
        const ggeResultRow = new ResultRow();
        ggeResultRow.label = this.translationService.get('RESULTS.DATA.RNG_PRODUCTION_GGE');
        ggeResultRow.values = Utils.deepCopy(row['RESULTS.DATA.VALUES']);
        ggeResultRow.values[0] = ggeValue;
        this.convertToPeriodAndSetDisplayName(ggeResultRow, ggeUnit);
        resultSet[label]['RESULTS.DATA.RNG_PRODUCTION_GGE'] = ggeResultRow;
      }
    } else {
      if (row['RESULTS.DATA.VALUES'] && row['RESULTS.DATA.VALUES'][0]) {
        const baseValue = row['RESULTS.DATA.VALUES'][0];
        const gjUnit = this.unitService.getUnitById('000000000000000000003814');
        const gwhUnit = this.unitService.getUnitById('000000000000000000003815');
        const kwhUnit = this.unitService.getUnitById('000000000000000000003818');
        const sm3Unit = this.unitService.getUnitById('000000000000000000003821');
        const gjValue = gjUnit.convertFromBase(Utils.deepCopy(baseValue));
        const gwhValue = gwhUnit.convertFromBase(Utils.deepCopy(baseValue));
        const kwhValue = kwhUnit.convertFromBase(Utils.deepCopy(baseValue));
        const sm3Value = sm3Unit.convertFromBase(Utils.deepCopy(baseValue));
        const gjResultRow = new ResultRow();
        gjResultRow.label = this.translationService.get('RESULTS.DATA.RNG_PRODUCTION_GJ');
        gjResultRow.values = Utils.deepCopy(row['RESULTS.DATA.VALUES']);
        gjResultRow.values[0] = gjValue;
        this.convertToPeriodAndSetDisplayName(gjResultRow, gjUnit);
        resultSet[label]['RESULTS.DATA.RNG_PRODUCTION_GJ'] = gjResultRow;
        const gwhResultRow = new ResultRow();
        gwhResultRow.label = this.translationService.get('RESULTS.DATA.RNG_PRODUCTION_GWH');
        gwhResultRow.values = Utils.deepCopy(row['RESULTS.DATA.VALUES']);
        gwhResultRow.values[0] = gwhValue;
        this.convertToPeriodAndSetDisplayName(gwhResultRow, gwhUnit);
        resultSet[label]['RESULTS.DATA.RNG_PRODUCTION_GWH'] = gwhResultRow;
        const kwhResultRow = new ResultRow();
        kwhResultRow.label = this.translationService.get('RESULTS.DATA.RNG_PRODUCTION_KWH');
        kwhResultRow.values = Utils.deepCopy(row['RESULTS.DATA.VALUES']);
        kwhResultRow.values[0] = kwhValue;
        this.convertToPeriodAndSetDisplayName(kwhResultRow, kwhUnit);
        resultSet[label]['RESULTS.DATA.RNG_PRODUCTION_KWH'] = kwhResultRow;
        // add Sm3 row (metric only)
        const sm3ResultRow = new ResultRow();
        sm3ResultRow.label = this.translationService.get('RESULTS.DATA.RNG_PRODUCTION_SM3');
        sm3ResultRow.values = Utils.deepCopy(row['RESULTS.DATA.VALUES']);
        sm3ResultRow.values[0] = sm3Value;
        this.convertToPeriodAndSetDisplayName(sm3ResultRow, sm3Unit);
        resultSet[label]['RESULTS.DATA.RNG_PRODUCTION_SM3'] = sm3ResultRow;
      }
    }
  }

  prepareFeedstockTableData() {
    this.showTreeTable = false;
    this.treeTableData = {};
    this.curTableHeaders = [];
    this.curGraphData = [];

    if (!this.selectedResultItems || this.selectedResultItems.length < 1 || this.selectedScenarios.length < 1) {
      return;
    }

    // get baseline
    const baseLineLabel = this.translationService.get('SCENARIO_COMPARISON.RESULTS.BASELINE');
    let myRow = this.getFeedTableRow(this.feedstockService.getFeedstocks());

    const tableData: Record<string, Record<string, ResultRow>> = {};
    tableData[baseLineLabel] = myRow;

    // get scenarios
    for (const sce of this.scenarios) {
      if (this.selectedScenarios.includes(sce.name)) {
        myRow = this.getFeedTableRow(sce['project-state'].feedstocks);
        tableData[sce.name] = myRow;
      }
    }

    this.curTableHeaders = [this.valueHeader, this.shareHeader, this.unitHeader];
    this.treeTableData = tableData;
    this.showTreeTable = true;
  }

  getFeedTableRow(sceFeedstocks: Feedstock[]) {
    this.scenarioResultsService.processScenarioFeedstocks(sceFeedstocks);
    let feedstocks = this.scenarioResultsService.getFeedstockBreakdownData();
    if (!feedstocks) return;
    if (this.techUnitType === 'imperial') {
      feedstocks = this.scenarioResultsService.convertFeedstockBreakdownDataToImperial(feedstocks);
    }
    const tempData: Record<string, ResultRow> = {};
    for (const feed of feedstocks) {
      if (this.selectedResultItems.find(res => res.name === feed.feedstock)) {
        tempData[feed.feedstock] = this.convertBreakdownItemToTableItem(feed);
        const feedUnit = this.unitService.getUnitByName(feed.unit);
        this.convertToPeriodAndSetDisplayName(tempData[feed.feedstock], feedUnit);
      }
    }
    return tempData;
  }

  private convertBreakdownItemToTableItem(item: FeedstockBreakdownDataItem) {
    const tableItem = {
      'RESULTS.DATA.LABEL': item.feedstock,
      'RESULTS.DATA.VALUES': [+item.amount],
      'RESULTS.GENERAL.SHARE': item.share,
      'RESULTS.DATA.UNIT': item.unit
    };
    return new ResultRow(tableItem);
  }

  prepareFinGeneralTableData() {
    this.showTreeTable = false;
    this.treeTableData = {};
    this.curTableHeaders = [];
    this.curGraphData = [];

    if (!this.selectedResultItems || this.selectedResultItems.length < 1 || this.selectedScenarios.length < 1
      || !this.selectedResultGroup || !this.selectedResultGroup.dataFunc
    ) {
      return;
    }

    const datapackage = this.getFinGeneralDataPackage();
    if (!datapackage) return;
    // baseline
    const data = this.selectedResultGroup.dataFunc(this.selectedCurrency, datapackage.baselineDataset);
    if (!data.hasTableData) {
      return;
    }
    const baseLineLabel = this.translationService.get('SCENARIO_COMPARISON.RESULTS.BASELINE');
    const myRow = {};
    let tempData = {};
    this.valueKey = data.tableHeaders[0] === 'average' ? 'RESULTS.GENERAL.AVERAGE' : 'RESULTS.GENERAL.COST';
    for (const res of data.tableData) {
      if (this.selectedResultItems.find(i => i.name === res.label)) {
        myRow['RESULTS.DATA.LABEL'] = Utils.processStringForTranslation(res.label, this.translationService);
        myRow[this.valueKey] = res.values[0];
        myRow['RESULTS.GENERAL.SHARE'] = res.values['share'];
        tempData[myRow['RESULTS.DATA.LABEL']] = { ...myRow };
      }
    }
    const tableData = {};
    tableData[baseLineLabel] = tempData;
    this.curTableHeaders = [this.valueKey, 'RESULTS.GENERAL.SHARE'];

    // scenarios
    for (const sce of this.scenarios) {
      if (this.selectedScenarios.includes(sce.name)) {
        tempData = {};
        const scenarioDataset = this.getScenarioDataset(datapackage, sce);
        const data = this.selectedResultGroup.dataFunc(this.selectedCurrency, scenarioDataset);
        for (const res of data.tableData) {
          if (this.selectedResultItems.find(i => i.name === res.label)) {
            myRow['RESULTS.DATA.LABEL'] = Utils.processStringForTranslation(res.label, this.translationService);
            myRow[this.valueKey] = res.values[0];
            myRow['RESULTS.GENERAL.SHARE'] = res.values['share'];
            tempData[myRow['RESULTS.DATA.LABEL']] = { ...myRow };
          }
        }
        tableData[sce.name] = tempData;
      }
    }

    this.treeTableData = tableData;
    this.showTreeTable = true;
  }

  /**
   * Get the scenario result set that will be provided to the data processing function for table
   * and graph creation
   * @param datapackage a data package that contains info used to determine the proper result set
   * @param sce the current scenario
   * @private
   */
  private getScenarioDataset(datapackage: { baselineDataset: any; useResults: boolean }, sce: Scenario) {
    if (datapackage.useResults) {
      return sce.results;
    } else {
      if (this.selectedResultGroup?.name === 'RESULTS.SECTIONS.CAPEX_BREAKDOWN') {
        return { project: sce['project-state'], results: sce.results };
      }
      return sce['project-state'];
    }
  }

  prepareDataForGroupGraph() {
    this.curGraphData = [];
    const tempGraphData: PlotlyTrace[] = [];
    if (!this.selectedResultItems || !this.baseline) return;

    for (const selection of this.selectedResultItems) {
      let res: LooseObject = this.baseline;
      if (!selection.path) continue;
      const separated = selection.path.split('->');
      for (let i = 0; i < separated.length; i++) {
        res = res[separated[i]];
      }
      if (!res) continue;
      let unit;
      let myRow: ResultRow;
      const leafKeys = [this.unitHeader, this.valueHeader];
      if (res instanceof ResultRow && leafKeys.every(leafKey => Object.keys(res).includes(leafKey))) {

        myRow = Utils.deepCopy(res);
        // add baseline
        unit = this.financialExchange.find(curr => curr.id === myRow.unit);
        if (this.selectedCategory === ResultCategory.TECHNICAL) {
          this.convertResultRowToTargetUnit(myRow);
          const rowUnit = this.unitService.getUnitByName(myRow.unit);
          this.convertToPeriodAndSetDisplayName(myRow, rowUnit);
        } else {
          this.processExchange(myRow);
          myRow.unit = this.setUnit(myRow.unit);
        }

      } else {
        const values: number[] = [];
        for (const sub of Object.keys(res)) {
          if (!res[sub]) continue;
          myRow = Utils.deepCopy(res[sub]);
          unit = this.financialExchange.find(curr => curr.id === myRow.unit);
          this.processExchange(myRow);
          myRow.unit = this.setUnit(myRow.unit);
          for (let i = 0; i < myRow.values.length; i++) {
            if (typeof values[i] === 'undefined') {
              values[i] = myRow.values[i];
            } else {
              values[i] += myRow.values[i];
            }
          }
        }
        myRow.values = [...values];
      }

      if (unit) {
        this.yAxisLabel = myRow.unit ?? '';
      } else {
        this.yAxisLabel = '';
      }

      const valuesDeepCopy = Utils.deepCopy(myRow.values);
      let traceFromDataRow = ResultsUtils.getTraceFromDataRow(valuesDeepCopy);
      traceFromDataRow['name'] = this.translationService.get(selection.name) + ' ' + this.translationService.get('SCENARIO_COMPARISON.RESULTS.BASELINE');
      traceFromDataRow.type = 'bar';
      tempGraphData.push(traceFromDataRow);

      // add scenarios
      for (const sce of this.scenarios) {
        if (this.selectedScenarios.includes(sce.name)) {
          let sceRes: LooseObject = sce.results;
          for (let i = 0; i < separated.length; i++) {
            sceRes = sceRes[separated[i]];
          }
          if (!sceRes) continue;
          const leafKeys = [this.unitHeader, this.valueHeader];
          if (sceRes instanceof ResultRow && leafKeys.every(leafKey => Object.keys(sceRes).includes(leafKey))) {
            myRow = Utils.deepCopy(sceRes);
            unit = this.financialExchange.find(curr => curr.id === myRow.unit);
            if (this.selectedCategory === ResultCategory.TECHNICAL) {
              this.convertResultRowToTargetUnit(myRow);
              const rowUnit = this.unitService.getUnitByName(myRow.unit);
              this.convertToPeriodAndSetDisplayName(myRow, rowUnit);
            } else {
              this.processExchange(myRow);
              myRow.unit = this.setUnit(myRow.unit);
            }

          } else {
            const values: number[] = [];
            for (const sub of Object.keys(sceRes)) {
              myRow = Utils.deepCopy(sceRes[sub]);
              unit = this.financialExchange.find(curr => curr.id === myRow.unit);
              this.processExchange(myRow);
              myRow.unit = this.setUnit(myRow.unit);
              for (let i = 0; i < myRow.values.length; i++) {
                if (typeof values[i] === 'undefined') {
                  values[i] = myRow.values[i];
                } else {
                  values[i] += myRow.values[i];
                }
              }
            }
            myRow.values = [...values];
          }
          const valuesDeepCopy = Utils.deepCopy(myRow.values);
          traceFromDataRow = ResultsUtils.getTraceFromDataRow(valuesDeepCopy);
          traceFromDataRow['name'] = this.translationService.get(selection.name) + ' ' + sce.name;
          traceFromDataRow.type = 'bar';
          tempGraphData.push(traceFromDataRow);
        }
      }
    }

    setTimeout(() => {
      this.curGraphData = [...tempGraphData];
      if (this.curGraphData && this.curGraphData.length > 0) {
        this.showGroupBarGraph = true;
      }

    });
  }

  prepareFinGeneralGraph() {
    this.curGraphData = [];

    if (!this.selectedResultItems || this.selectedResultItems.length < 1 || this.selectedScenarios.length < 1
      || !this.selectedResultGroup || !this.selectedResultGroup.dataFunc
    ) {
      return;
    }

    const data = this.selectedResultGroup.dataFunc(this.selectedCurrency, this.baseline);
    if (this.selectedResultGroup.name === 'RESULTS.SECTIONS.REVENUE_TREND') {
      // baseline
      for (const item of data.graphData) {
        if (this.selectedResultItems.find(res => res.name === item.name)) {
          item['name'] = this.translationService.get('SCENARIO_COMPARISON.RESULTS.BASELINE') + ' - ' +
            Utils.processStringForTranslation(item.name, this.translationService);
          this.curGraphData.push(item);
        }
      }

      // scenarios
      for (const sce of this.scenarios) {
        if (this.selectedScenarios.includes(sce.name)) {
          const data = this.selectedResultGroup.dataFunc(this.selectedCurrency, sce.results);
          for (const item of data.graphData) {
            if (this.selectedResultItems.find(res => res.name === item.name)) {
              item['name'] = sce.name + ' - ' + Utils.processStringForTranslation(item.name, this.translationService);
              this.curGraphData.push(item);
            }
          }
        }
      }
    } else if (this.selectedResultGroup.name === 'RESULTS.SECTIONS.EARNINGS_TREND') {
      // baseline
      for (const selection of this.selectedResultItems) {
        const trace: PlotlyTrace = {
          x: [],
          y: [],
          mode: 'lines',
          type: 'bar'
        };
        for (const item of data.graphData) {
          trace['name'] = this.translationService.get('SCENARIO_COMPARISON.RESULTS.BASELINE') + ' - ' +
            Utils.processStringForTranslation(selection.name, this.translationService);
          trace.x.push(Utils.processStringForTranslation(item.name, this.translationService));
          const res = item.series.find(i => i.name === selection.name);
          trace.y.push(res.value);
        }
        this.curGraphData.push(trace);
      }

      // scenarios
      for (const sce of this.scenarios) {
        if (this.selectedScenarios.includes(sce.name)) {
          const data = this.selectedResultGroup.dataFunc(this.selectedCurrency, sce.results);
          for (const selection of this.selectedResultItems) {
            const trace: PlotlyTrace = {
              x: [],
              y: [],
              mode: 'lines',
              type: 'bar'
            };
            for (const item of data.graphData) {
              trace['name'] = sce.name + ' - ' + Utils.processStringForTranslation(selection.name, this.translationService);
              trace.x.push(Utils.processStringForTranslation(item.name, this.translationService));
              const res = item.series.find(i => i.name === selection.name);
              trace.y.push(res.value);
            }
            this.curGraphData.push(trace);
          }
        }
      }
    }

    setTimeout(() => {
      this.curGraphData = [...this.curGraphData];
      if (this.curGraphData && this.curGraphData.length > 0) {
        this.showGroupBarGraph = true;
      }
    });
  }

  prepareDataForGroupPieChart() {
    this.showPieChart = false;
    this.curGraphData = [];
    this.graphLayout = {
      autosize: true,
      width: 1000,
      height: 600,
      xaxis: { title: '' },
      yaxis: { title: '' },
      title: '',
      legend: { 'orientation': 'h', x: -0.05, y: -0.3 },
      margin: { l: 50, r: 0, b: 70, t: 50 },
      grid: {
        rows: ((this.selectedScenarios.length + 1) > 2) ? 2 : 1,
        columns: this.selectedScenarios.length === 1 ? 2 : Math.ceil((this.selectedScenarios.length + 1) / 2)
      }
    };

    if (!this.selectedResultItems || this.selectedResultItems.length < 1 || this.selectedScenarios.length < 1) {
      return;
    }
    if (this.selectedCategory === ResultCategory.BREAKDOWN) {
      // get baseline chart
      const myRow = this.getScenarioPieChartData(this.translationService.get('SCENARIO_COMPARISON.RESULTS.BASELINE'),
        this.feedstockService.getFeedstocks());

      if (!myRow) return;
      this.curGraphData.push(myRow);

      // get scenario charts
      for (const sce of this.scenarios) {
        if (this.selectedScenarios.includes(sce.name)) {
          const myRow = this.getScenarioPieChartData(sce.name, sce['project-state'].feedstocks);
          if (!myRow) return;
          this.curGraphData.push(myRow);
        }
      }
    } else if (this.selectedCategory === ResultCategory.GENERAL) {
      const myRow = this.getScenarioPieChartData(this.translationService.get('SCENARIO_COMPARISON.RESULTS.BASELINE'));
      if (!myRow) return;
      this.curGraphData.push(myRow);

      for (const sce of this.scenarios) {
        if (this.selectedScenarios.includes(sce.name)) {
          const myRow = this.getScenarioPieChartData(sce.name, sce);
          if (!myRow) return;
          this.curGraphData.push(myRow);
        }
      }
    }

    setTimeout(() => {
      this.curGraphData = [...this.curGraphData];
      if (this.curGraphData && this.curGraphData.length > 0) {
        this.showPieChart = true;
      }
    });
  }

  getScenarioPieChartData(sceName: string, sce?: Feedstock[] | Scenario) {
    const chart: PieData = {
      values: [],
      labels: [],
      type: 'pie',
      name: sceName,
      domain: {
        row: this.curGraphData.length < Math.ceil((this.selectedScenarios.length + 1) / 2) ? 0 : 1,
        column: this.curGraphData.length % Math.max(Math.ceil((this.selectedScenarios.length + 1) / 2), 2)
      },
      sort: false
    };

    if (this.selectedCategory === ResultCategory.BREAKDOWN) {
      if (sce instanceof Scenario) return;
      this.scenarioResultsService.processScenarioFeedstocks(sce);
      let feedstocks = this.scenarioResultsService.getFeedstockBreakdownData();
      if (this.techUnitType === 'imperial') {
        feedstocks = this.scenarioResultsService.convertFeedstockBreakdownDataToImperial(feedstocks);
      }
      for (const feed of feedstocks) {
        const row = new ResultRow({
          'RESULTS.DATA.LABEL': feed.feedstock,
          'RESULTS.DATA.VALUES': [+feed.amount],
          'RESULTS.DATA.UNIT': feed.unit
        });
        const feedUnit = this.unitService.getUnitByName(feed.unit);
        const unitName = this.convertToPeriodAndSetDisplayName(row, feedUnit).unit;
        const amountValue = Utils.roundToPrecision(+Utils.removeCommas(row.values[0]),
          this.scenarioResultsService.getProjectDecimalPrecision());
        chart.values.push(amountValue);
        chart.labels.push(feed.feedstock);
        chart['hovertemplate'] = '%{label}<br>' +
          '%{value} ' + unitName + '<br>' +
          '%{percent:,.2%}' + '<extra>' + sceName + '</extra>';
      }
    } else if (this.selectedCategory === ResultCategory.GENERAL) {
      const datapackage = this.getFinGeneralDataPackage();
      if (!datapackage) return;
      let dataset;
      if (sce) {
        if (sce instanceof Scenario) {
          dataset = this.getScenarioDataset(datapackage, sce);
        }
      } else {
        dataset = datapackage.baselineDataset;
      }
      if (!this.selectedResultGroup?.dataFunc) return;
      const data = this.selectedResultGroup.dataFunc(this.selectedCurrency, dataset);
      for (const item of data.graphData) {
        chart.values.push(item.value);
        chart.labels.push(Utils.processStringForTranslation(item.name, this.translationService));
      }
      chart['hovertemplate'] = '%{label}<br>' +
        '%{value} ' + this.translationService.get(this.selectedCurrency?.translationId) + '<br>' +
        '%{percent:,.2%}' + '<extra>' + sceName + '</extra>';
    }
    chart['texttemplate'] = '%{percent:,.2%}';

    return chart;
  }

  /**
   * Converts result data based on the select currency
   * @param row result data row
   */
  processExchange(row: ResultRow): ResultRow {
    // Regex explanation https://regex101.com/r/usddk5/1
    const combinedUnitRegex = /(00000000000000000000(20|22)\d{2})/;
    const currencyUnitRegex = /(0000000000000000000020\d{2})/;
    const cpiUnitRegex = /(0000000000000000000022\d{2})/;

    // Check if CPI or currency and convert appropriately
    if (row.unit && row.unit.match(combinedUnitRegex) && this.selectedCurrency != null) {
      let currencyUnitId: string | null = null;

      if (row.unit.match(currencyUnitRegex)) {
        currencyUnitId = row.unit;
      } else if (row.unit.match(cpiUnitRegex)) {
        const cpiUnit = this.unitService.getUnitById(row.unit);
        const currencyUnit = this.unitService.getUnitByName(cpiUnit.displayName.slice(0, 3));
        currencyUnitId = currencyUnit.id;
      }

      // Only convert if results and selected currency are different
      if (currencyUnitId && currencyUnitId !== this.selectedCurrency.id) {
        for (let i = 0; i < row.values.length; i++) {
          row.values[i] = Unit.convertValue(row.values[i], currencyUnitId, this.selectedCurrency.id);
        }
      }
    }
    return row;
  }

  /**
   * Converts result data based on the selected measurement system
   * @param row result data row
   */
  setUnit(currUnit: string | null): any {
    let unit: Unit | null = null;
    if (currUnit) {
      unit = this.unitService.getUnitById(currUnit);
      if (unit) {
        currUnit = unit.displayName;
        // Regex explanation https://regex101.com/r/usddk5/1
        if (this.selectedCurrency != null && unit.id.match(/(00000000000000000000(20|22)\d{2})/)) {
          currUnit = this.selectedCurrency.displayName + currUnit?.slice(3);
        }
      } else {
        currUnit = 'n/a';
      }
    }
    return currUnit;
  }

  /**
   * Converts result data based on the select period
   * @param row ResultRow object
   * @param unit
   */
  // convertToPeriod(row: ResultRow): ResultRow {
  //   if (row.unit) {
  //     if (this.selectedPeriod === 'month') {
  //       if (row.unit.includes('/day')) {
  //         row.values[0] = row.values[0] * 30;
  //         row.unit = row.unit.replace('/day', '/month');
  //       }
  //     } else if (this.selectedPeriod === 'year') {
  //       if (row.unit.includes('/day')) {
  //         row.values[0] = row.values[0] * 365;
  //         row.unit = row.unit.replace('/day', '/year');
  //       }
  //     }
  //   }
  //   return row;
  // }

  convertToPeriodAndSetDisplayName(row: ResultRow, unitToConvert: Unit, forceConversion = false): any {
    let skipConversion = false;
    let translatedDisplayName = '';
    if (!unitToConvert) {
      return;
    }
    const unit = new Unit(unitToConvert);
    if (forceConversion && !unit.displayName.includes('/day') && !unit.displayName.includes('%')) {
      unit.displayName = unit.displayName + '/day';
    }
    // Don't convert period unit if retainPeriod flag set
    if (!unit.displayName.includes('/day') || row['retainPeriod'] || row.label === 'RESULTS.DATA.TECHNICAL.OLR') {
      translatedDisplayName = this.translationService.get(unit.translationId);
      skipConversion = true;
    }
    if (!skipConversion && unit.displayName.includes('/day')) {
      const unitTranslationHack = PeriodUnitMapper[unit.id];
      if (!unitTranslationHack) {
        console.log('add this to the unit hack: ' + unit.id + ' - ' + unit.displayName);
      }
      let periodTranslation = this.translationService.get('RESULTS.GENERAL.DAY');
      if (this.selectedPeriod === 'month') {
        periodTranslation = this.translationService.get('RESULTS.GENERAL.MONTH');
        row.values[0] = row.values[0] * 30;
      } else if (this.selectedPeriod === 'year') {
        periodTranslation = this.translationService.get('RESULTS.GENERAL.YEAR');
        row.values[0] = row.values[0] * 365;
      }
      translatedDisplayName = this.translationService.get(unitTranslationHack, { period: periodTranslation });
    }

    row.unit = Unit.parseUnitDisplayName(translatedDisplayName);
    return row;
  }

  /**
   * Converts result data based on the selected measurement system
   * @param row ResultRow object
   */
  convertResultRowToTargetUnit(row: ResultRow, forceConversion = false): any {
    if (!row) return;
    let imperialUnit: Unit | null = null;
    let metricUnit: Unit | null = null;
    let selectedUnit: Unit | null = null;
    if (row.unit) {
      metricUnit = this.unitService.getUnitById(row.unit);
      if (this.techUnitType === 'imperial') {
        imperialUnit = this.getImperialCounterpart(row.unit);
      }
      // Don't convert period for OLR units
      if (Number(metricUnit.unitType) === 36) {
        row['retainPeriod'] = true;
      }
      if (imperialUnit) {
        selectedUnit = imperialUnit;
      } else if (metricUnit) {
        imperialUnit = null;
        selectedUnit = metricUnit;
      } else {
        row.unit = 'n/a';
      }
    }
    if (row.values) {
      if (metricUnit && imperialUnit) {
        row.values[0] = metricUnit.convertToUnit(row.values[0], imperialUnit);
      }
    }
    if (selectedUnit) {
      this.convertToPeriodAndSetDisplayName(row, selectedUnit, forceConversion);
    }
    return row;
  }

  private getImperialCounterpart(unitId: string): Unit | null {
    let imperialUnitId = '';
    switch (unitId) {
      case '000000000000000000003601': // kg VS/m3/day
        imperialUnitId = '000000000000000000003604'; // lb VS/ft3/day
        break;
      case '000000000000000000001203': // t (tonne)
        imperialUnitId = '000000000000000000001204'; // t (short ton)
        break;
      case '000000000000000000004901': // ton/day
        imperialUnitId = '000000000000000000004902'; // short ton/day
        break;
      case '000000000000000000004905': // ton/year
        imperialUnitId = '000000000000000000004906'; // short ton/year
        break;
      case '000000000000000000001802': // m3
        imperialUnitId = '000000000000000000001803'; // ft3
        break;
      case '000000000000000000003803': // m3/day
        imperialUnitId = '000000000000000000003806'; // ft3/day
        break;
      case '000000000000000000003805': // Nm3/day
        imperialUnitId = '000000000000000000003804'; // scf/day
        break;
      case '000000000000000000003812': // Nm3/year
        imperialUnitId = '000000000000000000003816'; // scf/year
        break;
      case '000000000000000000003817': // Nm3 CH4/year
        imperialUnitId = '000000000000000000003819'; // scf CH4/year
        break;
      case '000000000000000000005801': // nm3/hour
        imperialUnitId = '000000000000000000005802'; // scfm
        break;
      case '000000000000000000003502': // kW
        imperialUnitId = '000000000000000000003504'; // MMBtu/hour
        break;
      case '000000000000000000003708': // kWh/day
      case '000000000000000000000401': // %
        return null;
      case '000000000000000000006501': // litre/day
        imperialUnitId = '000000000000000000006502'; // gal. US/day
        break;
      default:
        imperialUnitId = unitId;
    }
    return this.unitService.getUnitById(imperialUnitId);
  }

  onSearchClick() {
    if (!this.dataReady || !this.selectedResultItems) return;

    const origCat = this.selectedCategory;
    const origGrp = this.selectedResultGroup;
    const origItems = this.selectedResultItems;

    const items = [];
    for (const cat of this.resultCategories) {
      if (this.financialDisabled(cat)) {
        continue;
      }
      this.selectedCategory = cat;
      this.selectedCategoryChanged();
      for (const group of this.resultGroups) {
        this.selectedResultGroup = group;
        this.selectedGroupChanged();
        for (const item of this.resultItems) {
          items.push({
            display: Utils.processStringForTranslation(cat, this.translationService) + ', ' +
              Utils.processStringForTranslation(group.name, this.translationService) + ', ' +
              Utils.processStringForTranslation(item.name, this.translationService),
            cat: cat,
            group: group.name,
            name: item.name
          });
        }
      }
    }

    this.selectedCategory = origCat;
    this.selectedCategoryChanged();
    if (origGrp) {
      this.selectedResultGroup = this.resultGroups.find(item => item.name === origGrp.name);
      this.selectedGroupChanged();
    }
    if (origItems.length > 0) {
      for (const item of origItems) {
        const match = this.resultItems.find(i => i.name === item.name);
        if (match) {
          this.selectedResultItems.push(match);
        }
      }
      this.selectedItemsChanged();
    }


    const dialog = this.matDialog.open(
      ScenarioResultDropdownSearchComponent,
      {
        width: '50vw',
        height: '62vh',
        data: items
      }
    );

    dialog.afterClosed().pipe(take(1)).subscribe(res => {
      if (res) {
        this.selectedCategory = res.cat;
        this.selectedCategoryChanged();
        this.selectedResultGroup = this.resultGroups.find(item => item.name === res.group);
        this.selectedGroupChanged();
        this.selectedResultItems = [];
        const resultItem = this.resultItems.find(i => i.name === res.name);
        if (resultItem) {
          this.selectedResultItems.push(resultItem);
        }
        this.selectedItemsChanged();
      }
    });
  }

  /**
   * Checks if the name contains a dot separator to determine if it should be translated
   * @param name the name string that will be tested
   */
  isPossibleTranslationId(name: string) {
    if (!name) {
      return;
    }
    return name.includes('.');
  }

  /**
   * Returns a data package that contains the proper baseline result set and if the
   * scenario results or project state should be provided to the data processing function
   * @private
   */
  private getFinGeneralDataPackage(): { baselineDataset: any, useResults: boolean } | null {
    let baselineDataset;
    let useResults = false;
    if (!this.selectedResultGroup) return null;
    switch (this.selectedResultGroup.name) {
      case 'RESULTS.SECTIONS.OPEX_BREAKDOWN':
      case 'RESULTS.SECTIONS.REVENUES_BREAKDOWN':
      case 'RESULTS.SECTIONS.SELLING_PRODUCTS_REVENUE_BREAKDOWN':
      case 'RESULTS.SECTIONS.FEEDSTOCK_REVENUE_BREAKDOWN':
      case 'RESULTS.SECTIONS.ADDITIONAL_INCOME_BREAKDOWN':
      case 'RESULTS.SECTIONS.REVENUE_TREND':
      case 'RESULTS.SECTIONS.EARNINGS_TREND':
        baselineDataset = this.baseline;
        useResults = true;
        break;
      case 'RESULTS.SECTIONS.CAPEX_BREAKDOWN':
        baselineDataset = { project: this.resultProject, results: this.baseline };
        useResults = false;
    }
    return { baselineDataset: baselineDataset, useResults: useResults };
  }

  /**
   * THIS IS All TEMP FOR THE INCOME STATEMENT TO ALLOW THE SCENARIO RESULTS TO BE USED WITH
   * VARIABLE PRICING
   */

  private prepareDataForTreeTable() {
    this.showTreeTable = false;
    this.treeTableData = {};
    this.curTableHeaders = [];
    this.curGraphData = [];
    const res = this.baseline;

    if (!res || this.selectedScenarios.length === 0 || !this.selectedResultItems || this.selectedResultItems.length === 0) {
      return;
    }

    // baseline
    const baseLineLabel = this.translationService.get('SCENARIO_COMPARISON.RESULTS.BASELINE');
    let myRow = this.getNodeFromResults(res, this.selectedResultItems);

    const tableData = {};
    tableData[baseLineLabel] = myRow;


    // scenarios
    for (const sce of this.scenarios) {
      if (this.selectedScenarios.includes(sce.name)) {
        myRow = this.getNodeFromResults(sce.results, this.selectedResultItems);
        tableData[sce.name] = myRow;
      }
    }
    if (this.selectedCategory !== ResultCategory.TECHNICAL && this.selectedCategory !== ResultCategory.METRICS) {
      this.curTableHeaders = null;
    } else {
      this.curTableHeaders = [this.valueHeader, this.unitHeader];
    }
    if (this.curTableHeaders) this.curTableHeaders = [...this.curTableHeaders];
    this.treeTableData = tableData;
    this.showTreeTable = true;
    const aggWhitelist = [
      'RESULTS.DATA.ISPL.REVENUES',
      'RESULTS.DATA.ISPL.EXPENSES'
    ];
    this.aggWhitelist = [];
    if (this.selectedResultGroup.name === 'RESULTS.DATA.TECHNICAL.SP') {
      this.aggMethod = AggregationMethod.FIRST;
      this.aggWhitelist = ['RESULTS.DATA.TECHNICAL.RNGP'];
    } else if (aggWhitelist.includes(this.selectedResultGroup.name)) {
      this.aggMethod = AggregationMethod.SUM;
    } else {
      this.aggMethod = AggregationMethod.NONE;
    }
  }

  private getNodeFromResults(res: Results, selection: ResultGroup[]) {
    if (!selection) {
      return null;
    }
    return this.getTreeTableData(res, selection);
  }

  private getTreeTableData(scenario: Results, selections: ResultGroup[]) {
    const result: LooseObject = {};
    let vals: LooseObject;
    for (const selection of selections) {
      if (!selection.path) continue;
      const separated = selection.path.split('->');
      vals = scenario;
      if (separated.length === 0) {
        throw Error('traversal error in scenario tree table generation');
      }
      for (let i = 0; i < separated.length; i++) {
        vals = vals[separated[i]];
      }
      if (!vals) continue;
      if (separated[1] === 'RESULTS.DATA.ISPL.TITLE') {
        if (!result[separated[separated.length - 2]]) result[separated[separated.length - 2]] = {};
        result[separated[separated.length - 2]][separated[separated.length - 1]] = Utils.deepCopy(vals);
      } else {
        result[separated[separated.length - 1]] = Utils.deepCopy(vals);
      }

      if (this.selectedCategory === ResultCategory.TECHNICAL) {
        let forceConvesion = false;
        if (this.selectedResultGroup) {
          if (this.selectedResultGroup.name === 'RESULTS.DATA.TECHNICAL.UTILS' || this.selectedResultGroup.name === 'RESULTS.DATA.TECHNICAL.ADDITIONAL_PRODUCTS') {
            forceConvesion = true;
          }
        }
        this.convertResultRowToTargetUnit(result[selection.name], forceConvesion);

        if (selection.name === 'RESULTS.DATA.TECHNICAL.RNGP' || selection.name === 'RESULTS.DATA.TECHNICAL.UTILS') {
          if (vals instanceof ResultRow) {
            this.addAdditionalRNGRows(result, Utils.deepCopy(vals));
          }
        }
      }

    }
    const resultCopy = Utils.deepCopy(result);
    this.convertValuesCurrency(resultCopy);
    return resultCopy;
  }

  private convertValuesCurrency(result) {
    if (!result) return;
    if (this.selectedCategory === ResultCategory.TECHNICAL) return;
    const leafKeys = [this.unitHeader, this.valueHeader];
    if (leafKeys.some(leafKey => Object.keys(result).includes(leafKey))) { // results object is a leaf
      const values = result.values;
      const currency = result.unit;
      if (!values || !currency) return;
      for (let i = 0; i < values.length; i++) {
        // if unit is cad currency, convert from base to target currency
        if (currency === '000000000000000000002004' || currency === '000000000000000000002205') {
          if (this.selectedCurrency != null) values[i] = this.selectedCurrency.convertFromBase(values[i]);
        } else {
          // check if unit is currency, and convert it from its current unit to target unit
          // if percentage units, skip
          if (['0000000000000000000002101', '0000000000000000000002201'].includes(currency)) continue;
          // if unit is a currency unit type
          const foundUnit = this.unitService.getUnitById(currency);
          if (!foundUnit) throw Error('unit not found with the the id found in the results');
          const currencyUnitTypeIds = [
            '000000000000000000000020', '000000000000000000000021', '000000000000000000000022'
          ];
          if (currencyUnitTypeIds.includes(foundUnit.unitType)) {
            const baseValue = foundUnit.convertToBase(values[i]);
            if (this.selectedCurrency != null) values[i] = this.selectedCurrency.convertFromBase(baseValue);
          }
        }
      }
      result.unit = this.setUnit(currency);
    } else {
      for (const subResult of Object.values(result)) {
        this.convertValuesCurrency(subResult);
      }
    }
  }
}

interface ResultGroup {
  name: string;
  path?: string | null;
  params?: {
    '%s': string[]
  };
  graphType?: string;
  dataFunc?: Function;
}
